import React from 'react';
import {
  TextField,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  // Alert,
  Button,
} from '@mui/material';
import { ClearAll } from '@mui/icons-material';

interface CommercialDueDiligenceData {
  reputationalConcerns: string;
  reputationalConcernsDetails: string;
  researchDate: string;
  searchTermsTools: string;
  experienceCredentials: string;
  canProvideReferences: string;
  references: string;
  hasRequiredLicenses: string;
  licenseExplanation: string;
}

interface CommercialDueDiligenceFormProps {
  data: CommercialDueDiligenceData;
  onChange: (data: CommercialDueDiligenceData) => void;
  disabled?: boolean;
  estimatedContractAmount?: string;
  errors?: { field: string; message: string }[];
}

const CommercialDueDiligenceForm: React.FC<CommercialDueDiligenceFormProps> = ({ data, onChange, disabled = false, estimatedContractAmount = '0', errors = [] }) => {
  const getFieldError = (fieldName: string) => {
    return errors.find(err => err.field === fieldName)?.message;
  };
  const handleChange = (field: keyof CommercialDueDiligenceData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...data,
      [field]: event.target.value,
    });
  };

  const handleClear = () => {
    onChange({
      reputationalConcerns: '',
      reputationalConcernsDetails: '',
      researchDate: '',
      searchTermsTools: '',
      experienceCredentials: '',
      canProvideReferences: '',
      references: '',
      hasRequiredLicenses: '',
      licenseExplanation: '',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">C. Commercial Due Diligence Checklist</Typography>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<ClearAll />}
          onClick={handleClear}
          size="small"
        >
          Clear Form
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <FormControl component="fieldset" disabled={disabled} error={!!getFieldError('reputationalConcerns')}>
          <FormLabel component="legend">
            1a. Did your research identify fraud, litigation, regulatory action, or serious reputational concerns?
          </FormLabel>
          <RadioGroup
            value={data.reputationalConcerns}
            onChange={handleChange('reputationalConcerns')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('reputationalConcerns') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('reputationalConcerns')}
            </Typography>
          )}
        </FormControl>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If YES, then please explain in detail and provide links to online sources or provide relevant documents as attachments"
          value={data.reputationalConcernsDetails}
          onChange={handleChange('reputationalConcernsDetails')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('reputationalConcernsDetails')}
          helperText={getFieldError('reputationalConcernsDetails')}
        />

        <TextField
          required
          fullWidth
          type="date"
          label="1b. Please indicate the date this was researched"
          value={data.researchDate}
          onChange={handleChange('researchDate')}
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          disabled={disabled}
          error={!!getFieldError('researchDate')}
          helperText={getFieldError('researchDate')}
        />

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Please describe the specific search terms you used in your research and the tools you used to perform this background research (for example, name of internet search engine, subscription database, due diligence service provider, etc.)"
          value={data.searchTermsTools}
          onChange={handleChange('searchTermsTools')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('searchTermsTools')}
          helperText={getFieldError('searchTermsTools')}
        />

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="2a. Please explain in detail how it was determined the third party has the experience, credentials, capacity, and materials to provide the goods or services (site visit, interviews, research, etc.)"
          value={data.experienceCredentials}
          onChange={handleChange('experienceCredentials')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('experienceCredentials')}
          helperText={getFieldError('experienceCredentials')}
        />

        <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('canProvideReferences')}>
          <FormLabel component="legend">
            2b. Can the third party provide references for the quality of its services or goods?
          </FormLabel>
          <RadioGroup
            value={data.canProvideReferences}
            onChange={handleChange('canProvideReferences')}
            onFocus={(e) => e.preventDefault()}
          >
            <FormControlLabel value="YES" control={<Radio />} label="YES" />
            <FormControlLabel value="NO" control={<Radio />} label="NO" />
          </RadioGroup>
          {getFieldError('canProvideReferences') && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              {getFieldError('canProvideReferences')}
            </Typography>
          )}
        </FormControl>

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Please provide references below or explain the lack of references"
          value={data.references}
          onChange={handleChange('references')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('references')}
          helperText={getFieldError('references')}
        />

        {parseFloat(estimatedContractAmount?.replace(/[^0-9.]/g, '') || '0') > 25000 && (
          <FormControl component="fieldset" sx={{ mt: 2 }} disabled={disabled} error={!!getFieldError('hasRequiredLicenses')}>
            <FormLabel component="legend">
              3. Does the third party have all required licenses and government registrations to provide the services or goods?
            </FormLabel>
            <RadioGroup
              value={data.hasRequiredLicenses}
              onChange={handleChange('hasRequiredLicenses')}
              onFocus={(e) => e.preventDefault()}
            >
              <FormControlLabel value="YES" control={<Radio />} label="YES" />
              <FormControlLabel value="NO" control={<Radio />} label="NO" />
            </RadioGroup>
            {getFieldError('hasRequiredLicenses') && (
              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                {getFieldError('hasRequiredLicenses')}
              </Typography>
            )}
            <Typography variant="caption" sx={{ mt: 1 }}>
              If YES, then please obtain copies of such licenses and registrations and maintain them in a file for the third party.
            </Typography>
          </FormControl>
        )}

        <TextField
          fullWidth
          multiline
          rows={4}
          label="If NO, please explain in detail"
          value={data.licenseExplanation}
          onChange={handleChange('licenseExplanation')}
          variant="outlined"
          disabled={disabled}
          error={!!getFieldError('licenseExplanation')}
          helperText={getFieldError('licenseExplanation')}
        />
      </Box>
    </Box>
  );
};

export default CommercialDueDiligenceForm;
